<template>
  <v-container class="my-4" style="max-width: 700px;">
    <v-layout row wrap align-center class="mb-4">
      <v-flex>
        <h2 class="headline font-weight-bold">
          <v-icon left color="primary">history</v-icon>
          {{ $t('security_rounds_history') }}
        </h2>
      </v-flex>
      <v-spacer></v-spacer>
      <v-btn flat color="primary" @click="$router.push('/security/rounds')">
        <v-icon left small>arrow_back</v-icon>
        {{ $t('security_rounds') }}
      </v-btn>
    </v-layout>

    <v-progress-linear v-if="isLoading" indeterminate class="mb-3"></v-progress-linear>

    <v-alert v-if="securityError" type="error" :value="true" class="mb-4">
      {{ securityError }}
    </v-alert>

    <v-alert v-else-if="!isLoading && rounds.length === 0" type="info" :value="true">
      {{ $t('security_rounds_no_history') }}
    </v-alert>

    <v-expansion-panel v-else expand>
      <v-expansion-panel-content
        v-for="round in rounds"
        :key="round.id"
        class="mb-2 elevation-1 rounded-lg"
      >
        <template v-slot:header>
          <v-layout row wrap align-center>
            <v-avatar size="36" :color="statusColor(round.status) + ' lighten-4'" class="mr-3">
              <v-icon :color="statusColor(round.status)">{{ statusIcon(round.status) }}</v-icon>
            </v-avatar>
            <div style="flex:1;">
              <div class="font-weight-bold body-2">{{ round.geocerca_nombre }}</div>
              <div class="caption grey--text">{{ formatDateTime(round.fecha_inicio) }}</div>
            </div>
            <div class="text-xs-right mr-3">
              <v-chip :color="statusColor(round.status)" text-color="white" small class="mr-1">
                {{ statusLabel(round.status) }}
              </v-chip>
              <v-chip small color="blue-grey lighten-4" text-color="blue-grey darken-2">
                {{ round.checkpoints_visitados }}/{{ round.total_checkpoints }}
              </v-chip>
            </div>
          </v-layout>
        </template>

        <v-card flat>
          <v-card-text>
            <div class="mb-2">
              <strong>{{ $t('security_rounds_final_notes') }}:</strong> {{ round.notas || '-' }}
            </div>
            <div class="mb-3 caption grey--text">
              {{ formatDateTime(round.fecha_inicio) }} &rarr; {{ round.fecha_fin ? formatDateTime(round.fecha_fin) : $t('security_rounds_in_progress') }}
            </div>

            <v-list v-if="round.visitas && round.visitas.length > 0" dense two-line>
              <template v-for="(visita, index) in round.visitas">
                <v-list-tile :key="visita.id">
                  <v-list-tile-avatar>
                    <v-avatar size="36" v-if="visita.foto">
                      <img :src="visita.foto" :alt="visita.checkpoint_nombre">
                    </v-avatar>
                    <v-icon v-else color="grey">image_not_supported</v-icon>
                  </v-list-tile-avatar>
                  <v-list-tile-content>
                    <v-list-tile-title>{{ visita.checkpoint_nombre }}</v-list-tile-title>
                    <v-list-tile-sub-title>
                      {{ formatDateTime(visita.fecha_visita) }} &middot; {{ visita.distancia_formateada }}
                      <span v-if="visita.dentro_geocerca === false" class="error--text font-weight-bold">
                        &middot; {{ $t('security_rounds_outside_geofence') }}
                      </span>
                    </v-list-tile-sub-title>
                  </v-list-tile-content>
                </v-list-tile>
                <v-divider v-if="index < round.visitas.length - 1" :key="`div-${visita.id}`"></v-divider>
              </template>
            </v-list>
            <v-alert v-else type="info" :value="true" dense>
              {{ $t('security_rounds_no_visits') }}
            </v-alert>
          </v-card-text>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-container>
</template>

<script>
import { FETCH_ROUNDS_HISTORY } from '../../../store/modules/security'
import { USER_REQUEST } from '../../../store/actions/user'
import { mapGetters } from 'vuex'

export default {
  name: 'SecurityRoundsHistory',
  computed: {
    ...mapGetters(['getToken', 'getProfile']),
    ...mapGetters('security', ['isSecurityLoading', 'getSecurityError', 'getRoundsHistory']),
    isLoading() {
      return this.isSecurityLoading
    },
    securityError() {
      return this.getSecurityError
    },
    rounds() {
      return this.getRoundsHistory
    }
  },
  methods: {
    fetchHistory() {
      this.$store.dispatch(`security/${FETCH_ROUNDS_HISTORY}`, { token: this.getToken, limit: 30, offset: 0 })
    },
    statusColor(status) {
      const colors = { completado: 'success', en_progreso: 'info', cancelado: 'grey' }
      return colors[status] || 'grey'
    },
    statusIcon(status) {
      const icons = { completado: 'check_circle', en_progreso: 'schedule', cancelado: 'cancel' }
      return icons[status] || 'help'
    },
    statusLabel(status) {
      const labels = { completado: 'Completado', en_progreso: 'En Progreso', cancelado: 'Cancelado' }
      return labels[status] || status
    },
    formatDateTime(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString(this.$i18n.locale, {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
      })
    },
    validateAndFetch() {
      if (!this.getProfile || this.getProfile.roll !== 9) {
        this.$router.push('/')
        return
      }
      this.fetchHistory()
    }
  },
  mounted() {
    if (!this.$store.getters.isAuthenticated) {
      this.$router.push('/')
      return
    }

    if (!this.$store.getters.isProfileLoaded) {
      this.$store.dispatch(USER_REQUEST)
        .then(() => {
          this.validateAndFetch()
        })
        .catch(() => {
          this.$router.push('/')
        })
    } else {
      this.validateAndFetch()
    }
  }
}
</script>

<style scoped>
.mb-2 {
  margin-bottom: 12px;
}
.mb-3 {
  margin-bottom: 20px;
}
.mb-4 {
  margin-bottom: 28px;
}
</style>
